import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button, FormControlLabel,
  Switch, MenuItem, Snackbar, Alert, CircularProgress, Divider, Stack, Avatar,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { useNavigate, useParams } from 'react-router-dom';
import { marketingApi, salesExecutiveApi } from '../api/apiClient';
import PageShell from '../common/PageShell';

const emptyForm = {
  companyName: '', contactNumber: '', address: '', active: true, salesExecutiveId: '',
};

const SectionHeader = ({ icon, title, subtitle }) => (
  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
    <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, '& svg': { fontSize: 18 } }}>
      {icon}
    </Avatar>
    <Box>
      <Typography variant="subtitle1" fontWeight={700}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
  </Stack>
);

const MarketingForm = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [executives, setExecutives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    salesExecutiveApi
      .getAll()
      .then((res) => setExecutives(res.data || []))
      .catch(() => setExecutives([]));

    if (isEdit) {
      marketingApi.getById(id).then((res) => {
        const d = res.data;
        setForm({
          companyName: d.companyName || '',
          contactNumber: d.contactNumber || '',
          address: d.address || '',
          active: d.active ?? true,
          salesExecutiveId: d.salesExecutiveId || '',
        });
      });
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = {
      companyName: form.companyName,
      contactNumber: form.contactNumber,
      address: form.address,
      active: form.active,
      salesExecutiveId: form.salesExecutiveId ? Number(form.salesExecutiveId) : null,
    };
    try {
      if (isEdit) await marketingApi.update(id, payload);
      else await marketingApi.create(payload);
      setSnackbar({ open: true, message: 'Saved successfully', severity: 'success' });
      setTimeout(() => navigate('/marketing'), 800);
    } catch {
      setSnackbar({ open: true, message: 'Save failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell
      icon={isEdit ? <EditIcon /> : <AddCircleIcon />}
      title={isEdit ? 'Edit Marketing Record' : 'New Marketing Record'}
      subtitle={
        isEdit
          ? 'Update the company information and assignment below'
          : 'Fill in the details to create a new marketing record'
      }
      breadcrumbs={[
        { label: 'Dashboard', to: '/' },
        { label: 'Marketing', to: '/marketing' },
        { label: isEdit ? 'Edit' : 'New' },
      ]}
      actions={
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/marketing')}
          sx={{
            color: '#fff',
            borderColor: 'rgba(255,255,255,0.5)',
            '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
          }}
          variant="outlined"
        >
          Back
        </Button>
      }
    >
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Company info */}
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3 },
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                height: '100%',
              }}
            >
              <SectionHeader
                icon={<BusinessIcon />}
                title="Company Information"
                subtitle="Basic details about the company"
              />
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    name="companyName"
                    value={form.companyName}
                    onChange={handleChange}
                    required
                    size="small"
                    placeholder="e.g. Acme Corp"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Contact Number"
                    name="contactNumber"
                    value={form.contactNumber}
                    onChange={handleChange}
                    required
                    size="small"
                    placeholder="e.g. +91 98765 43210"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    name="address"
                    multiline
                    rows={3}
                    value={form.address}
                    onChange={handleChange}
                    size="small"
                    placeholder="Street, city, state, PIN"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Assignment + status */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3} sx={{ height: '100%' }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, sm: 3 },
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <SectionHeader
                  icon={<PersonIcon />}
                  title="Assignment"
                  subtitle="Assign a sales executive"
                />
                <TextField
                  select
                  fullWidth
                  label="Sales Executive"
                  name="salesExecutiveId"
                  value={form.salesExecutiveId}
                  onChange={handleChange}
                  size="small"
                >
                  <MenuItem value="">— Not assigned —</MenuItem>
                  {executives.map((ex) => (
                    <MenuItem key={ex.id} value={ex.id}>
                      {ex.firstName} {ex.lastName} ({ex.mobileNumber})
                    </MenuItem>
                  ))}
                </TextField>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, sm: 3 },
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <SectionHeader
                  icon={<AddCircleIcon />}
                  title="Status"
                  subtitle="Set record visibility"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.active}
                      name="active"
                      onChange={handleChange}
                      color="success"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={700}>
                        {form.active ? 'Active' : 'Inactive'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {form.active
                          ? 'Record is visible and in use'
                          : 'Record is hidden from active use'}
                      </Typography>
                    </Box>
                  }
                />
              </Paper>
            </Stack>
          </Grid>

          {/* Actions */}
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.50',
              }}
            >
              <Stack
                direction={{ xs: 'column-reverse', sm: 'row' }}
                spacing={1.5}
                justifyContent="flex-end"
              >
                <Button
                  variant="outlined"
                  onClick={() => navigate('/marketing')}
                  sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 120 }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={!loading && <SaveIcon />}
                  sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 140 }}
                >
                  {loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : isEdit ? (
                    'Update Record'
                  ) : (
                    'Create Record'
                  )}
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageShell>
  );
};

export default MarketingForm;